'use client';

/**
 * Phase 92 Plan 08 Task 2: CrewSplitAuditWidget.
 *
 * Dashboard widget that surfaces the Plan 92-01 daily-cron audit findings
 * to admins on the `/` page. Without it, awareness of orphan/duplicate
 * stops depends on the ops email; this is the in-app companion channel.
 *
 * Behavior:
 *   - Mount → GET /api/admin/crew-split-audit (Plan 06 endpoint, gated by
 *     'edit:schedule' permission via lib/auth-types.ts).
 *   - 403 → render null (widget self-hides for users without permission).
 *   - All-zero findings → render null (no clutter on healthy days).
 *   - Findings exist → render summary counts + "Run repair" button.
 *   - Click Run repair → POST /api/admin/crew-split-audit/repair, toast,
 *     re-fetch findings (the response's findings_after is also valid but
 *     a fresh GET keeps a single rendering code path).
 *
 * Auth gating happens at the endpoint layer; the widget just respects the
 * 403 by hiding. Server-side guard remains the source of truth.
 *
 * Stable-toast pattern (lesson from Plan 06):
 *   - Production useToast wraps `toast` in useCallback([], ...) — reference
 *     stable across renders. Tests mock useToast with a hoisted singleton
 *     vi.fn() to honor the same contract. This widget excludes `toast`
 *     from useEffect/useCallback deps with eslint-disable to keep both
 *     production AND test stability paths working.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link2, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useToast } from '@/components/ToastProvider';
import type { Findings } from '@/scripts/audit/crew-split-integrity';

const AUDIT_URL = '/api/admin/crew-split-audit';
const REPAIR_URL = '/api/admin/crew-split-audit/repair';

function totalFindings(f: Findings): number {
  return f.duplicates.length + f.orphan_leads.length + f.orphan_shadows.length + f.misrouted.length;
}

export function CrewSplitAuditWidget() {
  const { toast } = useToast();
  // null = not yet loaded; false = loaded-but-hidden (403 or zero); Findings = render.
  const [findings, setFindings] = useState<Findings | null | false>(null);
  const [repairing, setRepairing] = useState(false);

  const loadFindings = useCallback(async () => {
    try {
      const res = await fetch(AUDIT_URL);
      if (res.status === 403) {
        // User doesn't have 'edit:schedule' — widget self-hides.
        setFindings(false);
        return;
      }
      if (!res.ok) {
        // Unexpected server error — hide rather than render a broken widget.
        // The daily-cron email remains the always-on alert channel.
        setFindings(false);
        return;
      }
      const body = (await res.json()) as Findings;
      if (totalFindings(body) === 0) {
        setFindings(false);
        return;
      }
      setFindings(body);
    } catch {
      setFindings(false);
    }
  }, []);

  useEffect(() => {
    loadFindings();
  }, [loadFindings]);

  const handleRepair = useCallback(async () => {
    if (repairing) return;
    setRepairing(true);
    try {
      const res = await fetch(REPAIR_URL, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Repair failed' }));
        toast(err.error || 'Repair failed', 'error');
        return;
      }
      const result = (await res.json()) as {
        repaired: number;
        errors: string[];
        findings_after: Findings;
      };
      const stillPending = result.findings_after.misrouted.length;
      const message =
        stillPending > 0
          ? `Repaired ${result.repaired} · ${stillPending} misrouted still need review`
          : `Repaired ${result.repaired} crew-split findings`;
      toast(message);
      // Re-fetch so the widget either hides (clean) or shows the residual
      // misrouted findings the repair couldn't auto-fix.
      await loadFindings();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Repair failed', 'error');
    } finally {
      setRepairing(false);
    }
    // toast is stable via useCallback([],...) — see header comment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairing, loadFindings]);

  // Hidden states: not loaded yet, 403, or zero findings.
  if (findings === null || findings === false) {
    return null;
  }

  const dupCount = findings.duplicates.length;
  const orphanLeadCount = findings.orphan_leads.length;
  const orphanShadowCount = findings.orphan_shadows.length;
  const misroutedCount = findings.misrouted.length;
  const total = dupCount + orphanLeadCount + orphanShadowCount + misroutedCount;

  // Urgency: red if duplicates (data integrity risk), amber if orphans/misrouted.
  const tone = dupCount > 0 ? 'rose' : 'amber';
  const toneClasses =
    tone === 'rose'
      ? {
          bg: 'bg-rose-500/10',
          border: 'border-rose-500/20',
          text: 'text-rose-300',
          accent: 'text-rose-400',
        }
      : {
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/20',
          text: 'text-amber-300',
          accent: 'text-amber-400',
        };

  return (
    <div
      data-testid="crew-split-audit-widget"
      className={`mt-4 rounded-2xl border ${toneClasses.border} ${toneClasses.bg} p-4`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${toneClasses.accent}`}>
          <Link2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-bold uppercase tracking-tight ${toneClasses.text}`}>
              Crew-Split Integrity
            </h3>
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
              {total} finding{total === 1 ? '' : 's'}
            </span>
          </div>
          <p className="mt-1 text-xs text-zinc-300">
            <span className={dupCount > 0 ? toneClasses.accent + ' font-bold' : 'text-zinc-500'}>
              {dupCount} duplicate{dupCount === 1 ? '' : 's'}
            </span>
            <span className="text-zinc-700 mx-1.5">·</span>
            <span className={orphanLeadCount > 0 ? toneClasses.accent + ' font-bold' : 'text-zinc-500'}>
              {orphanLeadCount} orphan lead{orphanLeadCount === 1 ? '' : 's'}
            </span>
            <span className="text-zinc-700 mx-1.5">·</span>
            <span className={orphanShadowCount > 0 ? toneClasses.accent + ' font-bold' : 'text-zinc-500'}>
              {orphanShadowCount} orphan shadow{orphanShadowCount === 1 ? '' : 's'}
            </span>
            <span className="text-zinc-700 mx-1.5">·</span>
            <span className={misroutedCount > 0 ? 'text-amber-400 font-bold' : 'text-zinc-500'}>
              {misroutedCount} misrouted
            </span>
          </p>
          {misroutedCount > 0 && (
            <p className="mt-1.5 flex items-center gap-1.5 text-[10px] text-amber-400/80">
              <AlertTriangle className="h-3 w-3" />
              Misrouted findings are report-only — Run repair will not auto-fix
              them; review property_crew_splits manually.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleRepair}
          disabled={repairing}
          className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-zinc-900/60 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-200 hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {repairing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          {repairing ? 'Repairing…' : 'Run repair'}
        </button>
      </div>
    </div>
  );
}
